// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — descriptores PEMM-Empresa (Hammer 2007 · 0743)
// Capacidad de transformación de la ORGANIZACIÓN: 13 capacidades en 4 categorías
// (Liderazgo, Cultura, Conocimiento, Gobernanza) × 4 niveles E-1…E-4. Global =
// eslabón débil (MIN). Texto fiel a la matriz canónica de Hammer (Excel ESP).
// ⚠️ "Conciencia" y "Responsabilidad" se repiten entre categorías → cada capacidad
// lleva una CLAVE única (el backend la guarda en `capacidad`; el nombre se resuelve aquí).
// ════════════════════════════════════════════════════════════════════════════

export type ENivel = 'E1' | 'E2' | 'E3' | 'E4';
export type CategoriaEmpresa = 'LIDERAZGO' | 'CULTURA' | 'CONOCIMIENTO' | 'GOBERNANZA';

export interface NivelInfoEmpresa {
  nivel: ENivel;
  etiqueta: string;
  nombre: string;
  resumen: string;
  color: 'VERDE' | 'AMARILLO' | 'ROJO';
}

/** Los 4 niveles PEMM-Empresa de Hammer. Global de la empresa = eslabón débil (MIN). */
export const NIVELES_EMPRESA: NivelInfoEmpresa[] = [
  {
    nivel: 'E1',
    etiqueta: 'E-1',
    nombre: 'Inicial',
    resumen:
      'La capacidad opera a nivel inicial: el liderazgo, la cultura y el gobierno aún no sostienen la transformación por procesos.',
    color: 'ROJO',
  },
  {
    nivel: 'E2',
    etiqueta: 'E-2',
    nombre: 'En desarrollo',
    resumen:
      'Capacidad emergente: algún ejecutivo o área avanza, pero sin alineación organizacional plena.',
    color: 'AMARILLO',
  },
  {
    nivel: 'E3',
    etiqueta: 'E-3',
    nombre: 'Establecida',
    resumen:
      'Capacidad establecida: alineación estratégica y gobierno formalizado en toda la empresa.',
    color: 'VERDE',
  },
  {
    nivel: 'E4',
    etiqueta: 'E-4',
    nombre: 'Transformadora',
    resumen:
      'Capacidad transformadora y sostenible: la gestión por procesos es el modo de gestión, extendido a clientes y proveedores.',
    color: 'VERDE',
  },
];

export interface CategoriaInfoEmpresa {
  categoria: CategoriaEmpresa;
  nombre: string;
}
/** Las 4 categorías de capacidades PEMM-Empresa (4+4+2+3 = 13). */
export const CATEGORIAS_EMPRESA: CategoriaInfoEmpresa[] = [
  { categoria: 'LIDERAZGO', nombre: 'Liderazgo' },
  { categoria: 'CULTURA', nombre: 'Cultura' },
  { categoria: 'CONOCIMIENTO', nombre: 'Conocimiento' },
  { categoria: 'GOBERNANZA', nombre: 'Gobernanza' },
];

export interface CapacidadEmpresa {
  categoria: CategoriaEmpresa;
  clave: string;
  nombre: string;
}
/** Las 13 capacidades PEMM-Empresa en orden canónico (Hammer 2007). Clave ÚNICA. */
export const CAPACIDADES_EMPRESA: CapacidadEmpresa[] = [
  { categoria: 'LIDERAZGO', clave: 'lid_conciencia', nombre: 'Conciencia' },
  { categoria: 'LIDERAZGO', clave: 'lid_alineacion', nombre: 'Alineación' },
  { categoria: 'LIDERAZGO', clave: 'lid_comportamiento', nombre: 'Comportamiento' },
  { categoria: 'LIDERAZGO', clave: 'lid_estilo', nombre: 'Estilo' },
  { categoria: 'CULTURA', clave: 'cul_trabajo_equipo', nombre: 'Trabajo en equipo' },
  { categoria: 'CULTURA', clave: 'cul_enfoque_cliente', nombre: 'Enfoque en el cliente' },
  { categoria: 'CULTURA', clave: 'cul_responsabilidad', nombre: 'Responsabilidad' },
  { categoria: 'CULTURA', clave: 'cul_actitud_cambio', nombre: 'Actitud hacia el cambio' },
  { categoria: 'CONOCIMIENTO', clave: 'con_personas', nombre: 'Personas' },
  { categoria: 'CONOCIMIENTO', clave: 'con_metodologia', nombre: 'Metodología' },
  { categoria: 'GOBERNANZA', clave: 'gob_conciencia', nombre: 'Conciencia (modelo de procesos)' },
  { categoria: 'GOBERNANZA', clave: 'gob_responsabilidad', nombre: 'Responsabilidad' },
  { categoria: 'GOBERNANZA', clave: 'gob_integracion', nombre: 'Integración' },
];

/** Nota de método del modelo PEMM-Empresa. */
export const METODO_EMPRESA =
  'PEMM-Empresa (Hammer 2007): la organización alcanza el nivel E-n solo si TODAS sus capacidades llegan a E-n (eslabón débil). Una empresa en E-1 solo puede sostener procesos en P-1, por madura que sea la tecnología.';

/** Matriz 13×4: qué significa cada nivel E en cada capacidad (Hammer, texto canónico ESP). */
export const DESCRIPTORES_CAPACIDAD: Record<string, Record<ENivel, string>> = {
  lid_conciencia: {
    E1: 'Los altos ejecutivos reconocen la necesidad de mejorar el rendimiento, pero comprenden poco el poder de los procesos de negocio.',
    E2: 'Al menos un alto ejecutivo comprende a fondo el concepto de proceso, cómo mejora el rendimiento y lo que implica implementarlo.',
    E3: 'El equipo directivo ve la empresa en términos de proceso y ha desarrollado una visión de la empresa y de sus procesos.',
    E4: 'El equipo directivo ve su propio trabajo en términos de proceso y entiende la gestión por procesos como forma de gestionar el negocio.',
  },
  lid_alineacion: {
    E1: 'El liderazgo del programa de procesos reside en la gerencia media.',
    E2: 'Un alto ejecutivo ha asumido el liderazgo y la responsabilidad del programa de proceso.',
    E3: 'Fuerte alineación del equipo de Dirección sobre el programa de procesos; una red de personas lo promueve en toda la empresa.',
    E4: 'Personas de toda la empresa muestran entusiasmo por la gestión por procesos y ejercen liderazgo en sus esfuerzos.',
  },
  lid_comportamiento: {
    E1: 'Un alto ejecutivo respalda e invierte en la mejora operacional.',
    E2: 'Un alto ejecutivo fija públicamente objetivos en términos de cliente y compromete recursos, cambios y eliminación de obstáculos.',
    E3: 'Los altos ejecutivos operan en equipo, gestionan la empresa por sus procesos y participan activamente en el programa.',
    E4: 'El equipo realiza su trabajo como procesos, centrado en la planificación estratégica y en nuevas oportunidades de alto rendimiento.',
  },
  lid_estilo: {
    E1: 'El equipo directivo empieza a pasar de un estilo jerárquico a uno abierto y colaborativo.',
    E2: 'El equipo que lidera el programa es apasionado sobre la necesidad de cambio y los procesos como herramienta clave.',
    E3: 'El equipo ha delegado control y autoridad a los propietarios y ejecutantes de proceso.',
    E4: 'El equipo lidera mediante la visión y la influencia, en lugar de órdenes y control.',
  },
  cul_trabajo_equipo: {
    E1: 'El trabajo en equipo se centra en proyectos, es ocasional y atípico.',
    E2: 'La empresa usa equipos multidisciplinares con funcionalidades cruzadas para los esfuerzos de mejora.',
    E3: 'El trabajo en equipo es la norma entre los ejecutantes y un hábito común entre los gerentes.',
    E4: 'El trabajo en equipo con clientes y proveedores es un objetivo común.',
  },
  cul_enfoque_cliente: {
    E1: 'Se cree que el enfoque al cliente es importante, pero con poca comprensión y conflicto sobre cómo satisfacerlo.',
    E2: 'Los empleados entienden que el propósito de su trabajo es aportar un valor extraordinario al cliente.',
    E3: 'Los empleados entienden que los clientes exigen una excelencia uniforme y una experiencia transparente.',
    E4: 'Los empleados se enfocan en colaborar con socios (partners) para satisfacer al cliente final.',
  },
  cul_responsabilidad: {
    E1: 'La responsabilidad de los resultados recae en los gerentes.',
    E2: 'El personal de primera línea empieza a tomar propiedad de los resultados.',
    E3: 'Los empleados se sienten responsables de los resultados de la empresa.',
    E4: 'Los empleados tienen un sentido de misión en el servicio al cliente y en lograr un rendimiento cada vez mejor.',
  },
  cul_actitud_cambio: {
    E1: 'Creciente aceptación de la necesidad de hacer cambios modestos.',
    E2: 'Los empleados están preparados para un cambio significativo en la forma de trabajar.',
    E3: 'Los empleados están preparados para cambios multidimensionales importantes.',
    E4: 'Los empleados reconocen que el cambio es inevitable y lo abrazan como algo regular.',
  },
  con_personas: {
    E1: 'Un pequeño grupo de personas aprecia profundamente el poder de los procesos.',
    E2: 'Un cuadro de expertos tiene habilidades en rediseño, implementación, gestión de proyectos, comunicación y gestión del cambio.',
    E3: 'Un cuadro de expertos tiene habilidades en gestión del cambio y transformación de la empresa a gran escala.',
    E4: 'Muchas personas con habilidades de rediseño, programas y cambio en toda la empresa; hay un proceso formal para desarrollarlas.',
  },
  con_metodologia: {
    E1: 'La empresa usa una o más metodologías de resolución de problemas y hace mejoras incrementales.',
    E2: 'Los equipos de rediseño tienen acceso a una metodología básica de rediseño de procesos.',
    E3: 'La empresa ha estandarizado un proceso formal de rediseño integrado con uno de mejora continua.',
    E4: 'La gestión y el rediseño de procesos son competencias básicas embebidas en un sistema formal (entorno, planificación, innovación).',
  },
  gob_conciencia: {
    E1: 'La empresa ha identificado algunos procesos de negocio.',
    E2: 'La empresa ha desarrollado un modelo empresarial orientado a procesos, aceptado por el equipo ejecutivo.',
    E3: 'La gestión por procesos se ha comunicado en toda la empresa, impulsa la priorización y se alinea con la arquitectura de datos y tecnología.',
    E4: 'La empresa ha ampliado su modelo para conectar con clientes y proveedores, y lo usa en la estrategia.',
  },
  gob_responsabilidad: {
    E1: 'Los gerentes funcionales responden del rendimiento; los de proyecto, de las mejoras.',
    E2: 'Los propietarios de proceso responden de sus procesos y el comité directivo del progreso de todos.',
    E3: 'Los propietarios de proceso comparten la responsabilidad del desempeño de la empresa.',
    E4: 'Un consejo de procesos es el máximo órgano de gestión; hay comités con clientes y proveedores para el cambio interempresarial.',
  },
  gob_integracion: {
    E1: 'Uno o más grupos defienden técnicas posiblemente distintas para la mejora operacional.',
    E2: 'Un organismo informal coordina el programa mientras el comité directivo asigna recursos a los rediseños.',
    E3: 'Una oficina formal de gestión de programas, dirigida por un director de procesos, coordina e integra todos los proyectos.',
    E4: 'Los propietarios de proceso trabajan con sus contrapartes de clientes y proveedores para integrar procesos interempresariales.',
  },
};

/** Descriptor de una celda (capacidad × nivel). */
export function descriptorCapacidad(clave: string, nivel: ENivel): string {
  return DESCRIPTORES_CAPACIDAD[clave]?.[nivel] ?? '';
}
